import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@components/index";
import { toastService } from "@utils/index";
import { useAppConfig } from "@context/AppConfigContext";
import {
  getFilteredMenuItems,
  sidebarConfig,
  logoutModalConfig,
  handleNavigationLogic,
  getMenuItemClassName,
  getSidebarClassName,
  handleLogout,
} from "./sidebarData";
import "./index.scss";

const Sidebar = ({ isOpen, isCollapsed, isMobile, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppConfig();
  const [activePath, setActivePath] = useState(location.pathname);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Get filtered menu items based on user role
  const menuItems = getFilteredMenuItems(user?.user);

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location]);

  const handleNavigation = (path) => {
    handleNavigationLogic(path, navigate, setActivePath, onClose);
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    handleLogout(navigate, toastService);
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const showLabels = isMobile ? isOpen : !isCollapsed;

  return (
    <>
      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div className="sidebar-overlay" onClick={onClose} />
      )}

      <aside className={getSidebarClassName(isOpen, isCollapsed, isMobile)}>
        <div className="sidebar__header">
          {showLabels && (
            <h2 className="sidebar__logo">{sidebarConfig.logo}</h2>
          )}

          {isCollapsed && !isMobile && (
            <h2 className="sidebar__abbreviation">
              {sidebarConfig.abbreviation}
            </h2>
          )}
        </div>

        <nav className="sidebar__nav">
          <ul className="sidebar__menu">
            {menuItems.map((item) => (
              <li key={item.id} className="sidebar__menu-item">
                <button
                  className={getMenuItemClassName(activePath, item.path)}
                  onClick={() => handleNavigation(item.path)}
                  title={!showLabels ? item.label : undefined}
                >
                  <Icon
                    className="sidebar__menu-icon"
                    iconName={item.icon}
                    height={24}
                    width={24}
                  />
                  {showLabels && (
                    <span className="sidebar__menu-label">{item.label}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar__footer">
          <button
            className="sidebar__logout-btn"
            onClick={handleLogoutClick}
            title={!showLabels ? sidebarConfig.logoutLabel : undefined}
          >
            <Icon
              iconName={sidebarConfig.logoutIcon}
              height={24}
              width={24}
              className="sidebar__logout-icon"
            />
            {showLabels && (
              <span className="sidebar__logout-label">
                {sidebarConfig.logoutLabel}
              </span>
            )}
          </button>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="logout-modal-overlay" onClick={handleLogoutCancel}>
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="logout-modal__title">{logoutModalConfig.title}</h3>
            <p className="logout-modal__message">{logoutModalConfig.message}</p>
            <div className="logout-modal__actions">
              <button
                className="logout-modal__btn logout-modal__btn--cancel"
                onClick={handleLogoutCancel}
              >
                {logoutModalConfig.cancelText}
              </button>
              <button
                className="logout-modal__btn logout-modal__btn--confirm"
                onClick={handleLogoutConfirm}
              >
                {logoutModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
